import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split, type DocumentNode } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { authService } from '@features/auth/services/authService';
import { refreshToken } from './refreshToken';
import { navigateToLogin } from '@utils/navigateToLogin';
import { typePolicies } from './cacheConfig';
import { createAuthLink, createErrorLink, wsConnectionParams } from './links';
import siteConfig from '@config';
import { init, type GqlExecutor } from '@hopdrive/sdk-core';
import { setAmountDueExecutor } from '@features/invoices/services/amountDueExecutor';
import { requireDealerUpdate } from '@features/appUpdater/updateRequired';

const httpLink = createHttpLink({
  uri: siteConfig.graphqlHttpUrl,
});

// DEV ONLY: config.local.ts (gitignored) can set `devJwtOverride` to force a
// specific bearer token — used when pointing local dev at a PR-preview Hasura
// whose JWT audience/session may not line up with the local login flow. Never
// set in committed config; production builds never carry a local config.
const devJwtOverride: string | undefined = (siteConfig as { devJwtOverride?: string }).devJwtOverride;
if (devJwtOverride) {
  console.warn('[apollo] devJwtOverride is set — all GraphQL calls use the hard-coded dev token');
}
const getToken = async () => devJwtOverride || (await authService.getAccessTokenWithAutoRefresh());

const wsLink = new GraphQLWsLink(
  createClient({
    url: siteConfig.graphqlWsUrl,
    // connectionParams runs on every (re)connect, so a reconnect always picks
    // up a fresh token — the errorLink JWT-refresh retry below only covers
    // HTTP operations, not the socket.
    connectionParams: wsConnectionParams({ getToken, clientName: siteConfig.graphqlClientName }),
    // Without these, a dropped socket (laptop sleep, network blip, LB idle
    // timeout, Hasura closing on JWT expiry) leaves every subscription-backed
    // page dead until a full browser refresh.
    //
    // shouldRetry deliberately retries even auth-flavored (4xxx) closes:
    // Hasura closes with those on JWT expiry, and the reconnect's
    // connectionParams fetches a fresh token — that IS the fix for the most
    // common dead-socket cause. Do not gate on close codes here. A genuinely
    // revoked session fails in getAccessTokenWithAutoRefresh/refreshToken,
    // whose catch path navigates to login; until then the capped backoff
    // below bounds retries to ~1 handshake per 30s.
    keepAlive: 10_000,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    // Exponential backoff (capped at 30s, jittered) so a permanently broken
    // socket (auth revoked, Hasura unreachable) doesn't hammer the server,
    // and a backend restart doesn't get a thundering herd of reconnects.
    retryWait: retries =>
      new Promise(resolve =>
        setTimeout(resolve, Math.min(1_000 * 2 ** retries, 30_000) + Math.random() * 1_000)
      ),
    // Keep the socket open briefly after the last subscription unmounts so
    // navigating between live pages doesn't pay a full reconnect handshake.
    lazyCloseTimeout: 30_000,
  })
);

const authLink = createAuthLink({ getToken, clientName: siteConfig.graphqlClientName });

const errorLink = createErrorLink({ refreshToken, onAuthFailure: navigateToLogin, onUpdateRequired: requireDealerUpdate });

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const link = ApolloLink.from([errorLink, authLink, splitLink]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    typePolicies,
  }),
});

/**
 * App-owned Apollo executor: a thin adapter over this app's ApolloClient,
 * registered as sdk-core's default so every sdk-* package and the colocated
 * generated wrappers run through it. Reads use network-only (imperative callers
 * want fresh data); React hooks keep Apollo's cache-first default.
 */
export const gqlExecutor: GqlExecutor = {
  query: async (document, variables) =>
    (
      await client.query({
        query: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'network-only',
      })
    ).data,
  mutate: async (document, variables) =>
    (
      await client.mutate({
        mutation: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
      })
    ).data,
};
init({ executor: gqlExecutor, logger: console });

// The invoice screens' Amount Due reads skip the cache: they are used once, and
// writing each answer into the cache made Apollo re-read every watched query it
// touched, about 2.4 s a read with a large invoice on screen.
setAmountDueExecutor({
  query: async (document, variables) =>
    (
      await client.query({
        query: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'no-cache',
      })
    ).data,
  mutate: gqlExecutor.mutate,
});

export default client;
